import { X } from 'lucide-react'
import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { Button } from './Button'

export type ModalSize = 'sm' | 'md' | 'lg' | 'full' | 'screen'

/**
 * Чьё окно: `id` выбирает оттенок из токенов (`common`, `onec`, `aqsi`, `own`), `label` — слово источника в полосе под
 * заголовком, `detail` — имя ресурса, если источников одного вида несколько.
 */
export interface ModalSource {
  id: string
  label: string
  detail?: string
}

/**
 * Рамка модального окна. Щелчок мимо не закрывает: в окне идёт работа. Esc и
 * крестик закрывают. Накрытое окно неактивно целиком (`inert`).
 */
export function ModalFrame({
  title,
  size,
  covered,
  onClose,
  children,
  closeActionId,
  container,
  source,
}: {
  title: string
  size: ModalSize
  covered: boolean
  onClose?: () => void
  /** Контейнер рабочего окна: модальное окно центрируется внутри него. */
  container?: HTMLElement
  children: ReactNode
  /** Запись реестра действий для крестика (UI-ACTIONS): оболочка знает, как он называется в её реестре. */
  closeActionId?: string
  /** Источник окна: полоса «Источник · имя» и оттенок окна. Без него окно обычное. */
  source?: ModalSource
}) {
  const titleId = useId()
  const dialog = useRef<HTMLDivElement>(null)
  // Последний обработчик закрытия — в ref: новый onClose у родителя не должен
  // перезапускать эффект и уводить фокус из поля ввода внутри окна.
  const close = useRef(onClose)
  useEffect(() => {
    close.current = onClose
  }, [onClose])

  useEffect(() => {
    if (covered) return undefined
    if (!dialog.current?.contains(document.activeElement)) dialog.current?.focus()
    return undefined
  }, [covered])

  return createPortal(
    <div className="krp-modal-backdrop" style={container === undefined ? undefined : { position: 'absolute' }} inert={covered} aria-hidden={covered || undefined}>
      <div
        ref={dialog}
        className="krp-modal"
        data-size={size}
        data-source={source?.id}
        role="dialog"
        aria-modal={!covered}
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          event.preventDefault()
          event.stopPropagation()
          close.current?.()
        }}
      >
        {size !== 'screen' && (
          <div className="krp-modal__header">
            <h2 className="krp-modal__title" id={titleId}>
              {title}
            </h2>
            {onClose !== undefined && <Button
              variant="ghost"
              aria-label="Закрыть окно"
              icon={<X size={16} aria-hidden="true" />}
              onClick={onClose}
              actionId={closeActionId}
            />}
          </div>
        )}
        {source !== undefined && size !== 'screen' && (
          <div className="krp-modal__source" role="note">
            <span className="krp-modal__source-label">{source.label}</span>
            {source.detail !== undefined && source.detail !== '' && <span className="krp-modal__source-detail">{source.detail}</span>}
          </div>
        )}
        <div className="krp-modal__body">{children}</div>
      </div>
    </div>,
    container ?? document.body,
  )
}
